// BizPlus parity Phase 1.4 — shared document-conversion toolbar.
//
// Renders exactly the transitions the SERVER says this user may perform on
// this document right now. The list is never hardcoded per page: it comes from
// GET /api/documents/transitions/:type/:id, which intersects the shared
// registry (src/shared/documents/transitions.js) with the caller's permissions
// and the document's current status. A button that appears here will not 403.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ArrowRight, Check } from 'lucide-react';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { Button } from '@/shared/components/ui/Button';

export interface AvailableTransition {
  to: string;
  label: string;
  permission: string;
  alreadyDone: boolean;
}

export function useDocumentTransitions(docType: string, docId: string | null) {
  return useQuery({
    queryKey: ['document-transitions', docType, docId],
    queryFn: async () => {
      const { data } = await api.get<{ status: string; transitions: AvailableTransition[] }>(
        `/api/documents/transitions/${docType}/${docId}`,
      );
      return data;
    },
    enabled: !!docId,
  });
}

export function useRunTransition(docType: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, target }: { id: string; target: string }) => {
      const { data } = await api.post(`/api/documents/transitions/${docType}/${id}/to/${target}`);
      return data as { ok: boolean; document: { _id: string } };
    },
    onSuccess: () => {
      // A conversion changes both documents, so refresh broadly rather than
      // trying to guess which lists the new target belongs to.
      qc.invalidateQueries({ queryKey: ['document-transitions'] });
      qc.invalidateQueries({ queryKey: ['quotations'] });
      qc.invalidateQueries({ queryKey: ['proforma'] });
      qc.invalidateQueries({ queryKey: ['invoice-requests'] });
    },
  });
}

interface DocumentActionsProps {
  docType: string;
  docId: string;
  /** Called after a successful conversion, with the target type. */
  onConverted?: (target: string) => void;
  size?: 'sm' | 'md';
}

export function DocumentActions({ docType, docId, onConverted, size = 'sm' }: DocumentActionsProps) {
  const { data, isLoading } = useDocumentTransitions(docType, docId);
  const run = useRunTransition(docType);

  if (isLoading || !data?.transitions?.length) return null;

  return (
    <>
      {data.transitions.map((t) => (
        <Button
          key={t.to}
          size={size}
          variant="secondary"
          disabled={t.alreadyDone || run.isPending}
          title={t.alreadyDone ? 'Already converted' : t.label}
          onClick={() =>
            run.mutate(
              { id: docId, target: t.to },
              {
                onSuccess: () => { toast.success(`${t.label} — done.`); onConverted?.(t.to); },
                onError: (err) => toast.error(getApiErrorMessage(err, 'Conversion failed.')),
              },
            )
          }
        >
          {t.alreadyDone ? <Check className="h-3.5 w-3.5 mr-1" /> : null}
          {t.label}
          {!t.alreadyDone && <ArrowRight className="h-3.5 w-3.5 ml-1" />}
        </Button>
      ))}
    </>
  );
}
